import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { prefetchNavPath } from "@/app/routeModules";
import { recordQuickLinkCapture } from "@/lib/quickLinkCapture";
import { useUserOptional } from "@/auth/UserContext";
import { useAppBootstrap } from "@/context/AppBootstrapContext";
import { FeedProvider } from "@/context/FeedContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { useMobileShellViewport } from "@/hooks/useMobileShellViewport";
import { useNotificationAudioUnlock } from "@/hooks/useNotificationAudioUnlock";
import { AppShell } from "./AppShell";
import { PageTransition } from "./PageTransition";
import { PwaInstallInstructionsSheet } from "./PwaInstallInstructionsSheet";
import { ShellRouteOutlet } from "./ShellRouteOutlet";
import { SplashScreen } from "./SplashScreen";

/** Persistent shell — TopBar stays mounted so pill width can animate across routes. */
export function AppShellLayout() {
  const { pathname, search } = useLocation();
  const { isSplashVisible, phase } = useAppBootstrap();
  const profile = useUserOptional()?.profile;
  const isMobile = useMobileShellViewport();
  useNotificationAudioUnlock();
  const isKaiPage = pathname === "/";
  const isFeedPage = pathname.startsWith("/feed");
  // Desktop pages with DataTable sticky headers — flush scroll column so the
  // split header sticks at top: 0 (same as /bookings and /rates list).
  const isDesktopTableListPage =
    !isMobile &&
    (pathname === "/bookings" ||
      pathname.startsWith("/bookings/") ||
      pathname === "/rates" ||
      pathname.startsWith("/rates/") ||
      pathname === "/admin");
  // Kai owns the full width. Desktop feed docks Kai, an optional Filters card,
  // and a collapsed Notes card at the bottom. Mobile never opens Kai on feed —
  // Kai lives only on the Kai page.
  const hideRightRail = isKaiPage || isFeedPage;
  const [splashMounted, setSplashMounted] = useState(true);
  /** Latch — once exit starts, stay faded until unmount (avoid logo flash at ready). */
  const [splashExiting, setSplashExiting] = useState(false);

  useEffect(() => {
    if (phase === "exiting") {
      setSplashExiting(true);
    }
  }, [phase]);

  // Warm the landing route (chunk + first-page payload) while the splash is
  // up so /feed paints from cache instead of a skeleton → card double cascade.
  useEffect(() => {
    if (!profile) return;
    prefetchNavPath(pathname);
  }, [pathname, profile]);

  useEffect(() => {
    recordQuickLinkCapture(pathname, search);
  }, [pathname, search]);

  useEffect(() => {
    if (!isSplashVisible) {
      const timer = window.setTimeout(() => setSplashMounted(false), 120);
      return () => window.clearTimeout(timer);
    }
    setSplashMounted(true);
  }, [isSplashVisible]);

  return (
    <>
      <FeedProvider>
        <FavoritesProvider>
          <AppShell
            hideRightRail={hideRightRail}
            kaiPageLock={isKaiPage}
            kaiOnlyRail={isFeedPage}
            noTopScrollMask={isDesktopTableListPage}
          >
            <PageTransition>
              <ShellRouteOutlet />
            </PageTransition>
          </AppShell>
        </FavoritesProvider>
      </FeedProvider>
      {splashMounted && <SplashScreen exiting={splashExiting} />}
      <PwaInstallInstructionsSheet />
    </>
  );
}
